import React, { useMemo, useState, useEffect } from "react";
import {
  BadgeDollarSign,
  CheckCircle2,
  Clock3,
  Download,
  Filter,
  Package,
  RefreshCcw,
  Search,
  Truck,
  XCircle,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const Badge = ({ tone = "slate", children }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    yellow: "border-amber-200 bg-amber-50 text-amber-900",
    blue: "border-sky-200 bg-sky-50 text-sky-900",
    purple: "border-violet-200 bg-violet-50 text-violet-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    gray: "border-slate-200 bg-white text-slate-700",
  };

  return (
    <span className={cx("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black", tones[tone] || tones.slate)}>
      {children}
    </span>
  );
};

// Mock data - replace with actual API call
const MOCK_ORDERS = [
  {
    id: "ORD-001",
    customerName: "John Doe",
    customerEmail: "john@example.com",
    status: "pending",
    total: 125.5,
    items: 3,
    orderDate: "2024-01-15",
    shippingAddress: "123 Main St, New York, NY 10001",
    paymentMethod: "Credit Card",
  },
  {
    id: "ORD-002",
    customerName: "Jane Smith",
    customerEmail: "jane@example.com",
    status: "processing",
    total: 89.99,
    items: 2,
    orderDate: "2024-01-14",
    shippingAddress: "456 Oak Ave, Toronto, ON M5V 3A8",
    paymentMethod: "PayPal",
  },
  {
    id: "ORD-003",
    customerName: "Mike Johnson",
    customerEmail: "mike@example.com",
    status: "shipped",
    total: 245.0,
    items: 5,
    orderDate: "2024-01-13",
    shippingAddress: "789 High St, London, UK SW1A 1AA",
    paymentMethod: "Bank Transfer",
  },
  {
    id: "ORD-004",
    customerName: "Sarah Wilson",
    customerEmail: "sarah@example.com",
    status: "delivered",
    total: 67.25,
    items: 1,
    orderDate: "2024-01-12",
    shippingAddress: "321 Queen St, Sydney, NSW 2000",
    paymentMethod: "Credit Card",
  },
  {
    id: "ORD-005",
    customerName: "David Brown",
    customerEmail: "david@example.com",
    status: "cancelled",
    total: 156.75,
    items: 4,
    orderDate: "2024-01-11",
    shippingAddress: "654 Pine Rd, Vancouver, BC V6B 1A1",
    paymentMethod: "Credit Card",
  },
];

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setOrders(MOCK_ORDERS);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = orders;

    // Filter by search term
    if (orderFilter) {
      filtered = filtered.filter(order =>
        order.id.toLowerCase().includes(orderFilter.toLowerCase()) ||
        order.customerName.toLowerCase().includes(orderFilter.toLowerCase()) ||
        order.customerEmail.toLowerCase().includes(orderFilter.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filter by date
    if (dateFilter !== "all") {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case "today":
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(order => new Date(order.orderDate) >= filterDate);
          break;
        case "week":
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter(order => new Date(order.orderDate) >= filterDate);
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter(order => new Date(order.orderDate) >= filterDate);
          break;
        default:
          break;
      }
    }

    setFilteredOrders(filtered);
  }, [orders, orderFilter, statusFilter, dateFilter]);

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(order => order.id));
    }
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on orders:`, selectedOrders);
    // Implement bulk actions
  };

  const statusMeta = (status) => {
    const map = {
      pending: { tone: "yellow", Icon: Clock3 },
      processing: { tone: "blue", Icon: RefreshCcw },
      shipped: { tone: "purple", Icon: Truck },
      delivered: { tone: "green", Icon: CheckCircle2 },
      cancelled: { tone: "rose", Icon: XCircle },
      refunded: { tone: "gray", Icon: BadgeDollarSign },
    };
    return map[status] || { tone: "slate", Icon: Package };
  };

  const revenue = useMemo(
    () => orders.reduce((sum, order) => sum + (Number(order.total) || 0), 0),
    [orders]
  );

  const pendingCount = useMemo(() => orders.filter((o) => o.status === "pending").length, [orders]);
  const deliveredCount = useMemo(() => orders.filter((o) => o.status === "delivered").length, [orders]);

  return (
    <AdminPageShell
      title="Orders"
      description="Track and manage B2C customer orders. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge>
            <Package className="h-4 w-4" />
            {orders.length} total
          </Badge>
          <Badge tone="yellow">
            <Clock3 className="h-4 w-4" />
            {pendingCount} pending
          </Badge>
          <Badge tone="green">
            <CheckCircle2 className="h-4 w-4" />
            {deliveredCount} delivered
          </Badge>
          <Badge tone="gray">
            <BadgeDollarSign className="h-4 w-4" />
            ${revenue.toFixed(2)} revenue
          </Badge>
        </div>
      }
      actions={
        <>
          <SecondaryButton
            type="button"
            onClick={() => {
              setOrderFilter("");
              setStatusFilter("all");
              setDateFilter("all");
              setSelectedOrders([]);
            }}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={() => alert("Export coming soon")}
          >
            <Download className="h-4 w-4" />
            Export
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Package className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Total Orders</p>
                <p className="text-xl font-black text-slate-900">{orders.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-amber-200 bg-amber-50 flex items-center justify-center">
                <Clock3 className="h-5 w-5 text-amber-900" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Pending</p>
                <p className="text-xl font-black text-slate-900">{pendingCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-emerald-200 bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-800" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Delivered</p>
                <p className="text-xl font-black text-slate-900">{deliveredCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                <BadgeDollarSign className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Revenue</p>
                <p className="text-xl font-black text-slate-900">${revenue.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Order ID, customer name, email"
                  className={cx(FieldBase, "pl-10")}
                  value={orderFilter}
                  onChange={(e) => setOrderFilter(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <select
                  className={cx(SelectBase, "pl-10")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Date</label>
              <select className={SelectBase} value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
              </select>
            </div>
            <div className="flex items-end">
              <PrimaryButton type="button" className="w-full justify-center" onClick={() => alert("Export coming soon")}
              >
                <Download className="h-4 w-4" />
                Export Orders
              </PrimaryButton>
            </div>
          </div>

          {selectedOrders.length > 0 ? (
            <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">{selectedOrders.length} selected</div>
              <div className="flex flex-wrap items-center gap-2">
                <PrimaryButton type="button" className="px-4" onClick={() => handleBulkAction("process")}
                >
                  Mark Processing
                </PrimaryButton>
                <SecondaryButton type="button" className="px-4" onClick={() => handleBulkAction("ship")}
                >
                  Mark Shipped
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  className="px-4 border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                  onClick={() => handleBulkAction("cancel")}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          ) : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-black tracking-tight text-slate-900">Orders</p>
              <p className="text-sm text-slate-600">{filteredOrders.length} shown</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
              />
              Select all
            </label>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
              <p className="mt-2 text-slate-600">Loading orders…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Payment</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {filteredOrders.map((order) => {
                    const meta = statusMeta(order.status);
                    const StatusIcon = meta.Icon;

                    return (
                      <tr key={order.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => handleSelectOrder(order.id)}
                              className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30 mr-3"
                            />
                            <div>
                              <div className="text-sm font-semibold text-slate-900">{order.id}</div>
                              <div className="text-sm text-slate-600">
                                {order.items} items • {new Date(order.orderDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{order.customerName}</div>
                          <div className="text-sm text-slate-600">{order.customerEmail}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge tone={meta.tone}>
                            <StatusIcon className="h-4 w-4" />
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                          ${Number(order.total).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{order.paymentMethod}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-wrap items-center gap-2">
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => alert("View coming soon")}
                            >
                              View
                            </SecondaryButton>
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => alert("Edit coming soon")}
                            >
                              Edit
                            </SecondaryButton>
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => alert("Tracking coming soon")}
                            >
                              Track
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filteredOrders.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <p className="text-slate-600">No orders found matching your criteria.</p>
            </div>
          ) : null}
        </Card>
      </div>
    </AdminPageShell>
  );
};

export default OrderManagement;
